
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { useAudioRecording } from '@/hooks/useAudioRecording';
import { useAudioPlayback } from '@/hooks/useAudioPlayback';
import { uploadAndTranscribeAudio } from '@/utils/audioUpload';
import { RecordingState } from './voice/RecordingState';
import { PlaybackState } from './voice/PlaybackState';
import { BrandingSettings } from '@/hooks/useReferentBranding';

interface VoiceRecordingStepProps {
  updateFormData: (data: any) => void;
  branding?: BrandingSettings | null;
}

export const VoiceRecordingStep = ({ updateFormData, branding }: VoiceRecordingStepProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isUploadSuccess, setIsUploadSuccess] = useState(false);
  
  const {
    isRecording,
    audioBlob,
    audioUrl,
    recordingTime,
    MAX_RECORDING_TIME,
    startRecording,
    stopRecording,
    resetRecording
  } = useAudioRecording();

  const {
    isPlaying,
    audioRef,
    playAudio,
    pauseAudio,
    setIsPlaying
  } = useAudioPlayback(audioUrl);

  const handleUpload = async () => {
    if (!audioBlob) return;

    setIsUploading(true);
    try {
      await uploadAndTranscribeAudio(audioBlob, (audioFileUrl, audioTranscription, enrichedData) => {
        updateFormData({
          audioFileUrl,
          audioTranscription,
          enrichedData
        });
        setIsUploadSuccess(true);
      });
    } catch (_error) {
      // Error is already handled in uploadAndTranscribeAudio
    } finally {
      setIsUploading(false);
    }
  };

  const handleReset = () => {
    resetRecording();
    setIsPlaying(false);
    setIsUploadSuccess(false);
  };

  return (
    <div className="space-y-6 font-['Bricolage_Grotesque']">
      {/* Description sans titre (titre géré par le container) */}
      <div className="text-left space-y-4">
        <p className="text-gray-600">
          Enregistrez un court message vocal pour accompagner votre évaluation. Vous pouvez préciser :
        </p>
        <div className="text-gray-600 space-y-2 pl-4">
          <p>1) Le contexte de votre collaboration.</p>
          <p>2) Des exemples concrets de réussites ou d'axes d'amélioration.</p>
          <p>3) Des conseils pour faciliter son onboarding et sa future collaboration avec son manager.</p>
        </div>
        <p className="text-gray-600 font-medium">
          Notre IA se charge de la transcription.
        </p>
      </div>

      <Card>
        <CardContent className="p-8">
          <div className="text-center">
            {!audioUrl ? (
              <RecordingState
                isRecording={isRecording}
                recordingTime={recordingTime}
                maxRecordingTime={MAX_RECORDING_TIME}
                onStartRecording={startRecording}
                onStopRecording={stopRecording}
                branding={branding}
              />
            ) : (
              <PlaybackState
                audioUrl={audioUrl}
                recordingTime={recordingTime}
                isPlaying={isPlaying}
                isUploading={isUploading}
                isUploadSuccess={isUploadSuccess}
                audioRef={audioRef}
                onPlay={playAudio}
                onPause={pauseAudio}
                onReset={handleReset}
                onUpload={handleUpload}
                onPlayingEnd={() => setIsPlaying(false)}
                branding={branding}
              />
            )}
          </div>
        </CardContent>
      </Card>

    </div>
  );
};
